/**
 * Permission registry — single source of truth shared by the API
 * (Zod validation, audit narration, future enforcement middleware) and the
 * web app (permissions editor).
 *
 * A role owns an array of permission entries. Each entry has a `permission`
 * id from a registry, plus a `scope` if the registry entry declares scope
 * options. Scope is omitted entirely for global permissions
 * (e.g. `system_role.view`).
 *
 * The registry is parameterized so future use cases (API key scopes,
 * service-account permissions, …) can supply their own registry and reuse
 * the validator + editor + audit machinery without duplication.
 */
import type { FieldError } from './errors';

/**
 * The two scopes a scoped permission offers: **All** and **My team**. They mean
 * the same thing on every permission:
 *
 *   - `all`     → unrestricted.
 *   - `my_team` → "me and my team", where my team is my recursive HR reports
 *                 (`users.manager_id`). For employees that is me plus those
 *                 reports; for customers it is the customers I am on directly
 *                 OR any of those reports is on.
 *
 * The code says what the label says. Every code roles used to store — the
 * four customer codes, the timesheet ones, `report.view`'s lead-based ones and
 * `my_organization` itself (Employee's old "My team") — is listed in
 * {@link RETIRED_SCOPES}, and {@link canonicalScope} reads each as `my_team`.
 * See docs/permissions.md → Scopes.
 */
export const PERMISSION_SCOPES = ['all', 'my_team'] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/** Display label per scope code — the same on every permission. */
export const SCOPE_LABELS: Record<PermissionScope, string> = {
  all: 'All',
  my_team: 'My team',
};

/**
 * Scope codes roles used to store, with the label they were shown under. Kept
 * so a stored row that the cleanup migration has not reached yet is read
 * correctly ({@link canonicalScope}), and so old audit entries still read the
 * way they did when they were written ({@link scopeLabel}).
 *
 *   - `my_organization` — Employee's "My team" (me + my HR subtree), which
 *     `my_team` is now; on Customer/Timesheet it was my reports' customers
 *     WITHOUT my own, which had no successor and reads as `my_team` (adding
 *     the caller's own customers).
 *   - `my_customers` — customers I am a team member of.
 *   - `my_organization_my_customers` — that, plus my reports' customers. This is
 *     exactly what "My team" means for customers now.
 *   - `my_teams` / `my_organization_my_teams` — `report.view`'s lead-based
 *     scopes. `report.view` has no scope any more.
 */
export const RETIRED_SCOPES = {
  my_organization: 'My team',
  my_customers: 'My customers',
  my_organization_my_customers: 'My organization + My customers',
  my_teams: 'My team (lead)',
  my_organization_my_teams: 'My organization + My team (lead)',
} as const satisfies Record<string, string>;

/** Display label for a scope code. Retired codes (old audit entries) keep their
 *  old label; anything else unknown is shown as stored. */
export function scopeLabel(scope: string): string {
  return (
    (SCOPE_LABELS as Record<string, string>)[scope] ??
    (RETIRED_SCOPES as Record<string, string>)[scope] ??
    scope
  );
}

/**
 * The scope a stored grant of `permission` really carries, given whatever the
 * row holds. The ONE place stored scopes are interpreted — used by the
 * permission resolver (enforcement), by `normalizePermissions` (the role editor
 * and the role API's reads), so a row the cleanup migration has not reached yet
 * behaves exactly as it will after it.
 *
 *   - Unknown permission, or one the registry declares scopeless (`report.view`
 *     today) → `undefined`: presence is the whole grant.
 *   - A scope the permission offers → itself.
 *   - No scope at all on a scoped permission → `all`. That is how such a legacy
 *     entry has always been enforced.
 *   - ANY other value — a retired code, or garbage → `my_team`, when
 *     the permission offers it. Never `all`: reading a code this release does
 *     not know as "unrestricted" would silently widen a Manager's access, so an
 *     unknown restriction stays a restriction.
 */
export function canonicalScope(permission: string, scope: unknown): PermissionScope | undefined {
  const def = DEFAULT_INDEX.get(permission);
  return def ? scopeFor(def, scope) : undefined;
}

function scopeFor(def: PermissionDef, scope: unknown): PermissionScope | undefined {
  if (def.scopes.length === 0) return undefined;
  if (scope === undefined || scope === null) {
    return def.scopes.includes('all') ? 'all' : def.scopes[0];
  }
  if (typeof scope === 'string' && def.scopes.includes(scope as PermissionScope)) {
    return scope as PermissionScope;
  }
  return def.scopes.includes('my_team') ? 'my_team' : def.scopes[0];
}

/**
 * Tier controls who can grant a permission.
 *
 * - `tenant` (default): grantable by a tenant admin to any role inside their
 *   own tenant. The default for resources owned by the tenant.
 * - `platform`: too sensitive for tenant admins to hand out — these are
 *   granted only by platform operators via SQL or a future admin tool, and
 *   are rejected if they appear in a tenant-context grant request.
 *
 * Currently platform-tier: `tenant.*` only (the "edit your own tenant"
 * surface — domains[] in particular reroutes SSO across tenants). `audit.view`
 * is tenant-tier: audit data is tenant-scoped, so each tenant's admin may hold
 * it; the per-entity `.view` remains the fine-grained gate on which rows show.
 */
export type PermissionTier = 'tenant' | 'platform';

export interface PermissionDef {
  /** Dotted id stored in the DB (e.g., 'customer.sow.edit'). */
  readonly id: string;
  /** Resource group used to lay out the editor. */
  readonly group: string;
  /** Action label within the group. */
  readonly label: string;
  /**
   * Allowed scopes for this permission. Empty array = global, scope must be
   * omitted. Otherwise scope is required and must be one of these values.
   */
  readonly scopes: readonly PermissionScope[];
  /** Who can grant this permission. Defaults to 'tenant' when omitted. */
  readonly tier?: PermissionTier;
  /**
   * Marker for permissions registered ahead of their endpoint. Excluded
   * from the route-coverage CI check; otherwise behaves like any other id.
   */
  readonly pending?: boolean;
  /**
   * Three-way tier ladder:
   *   - default (no flag)    → tenant-grantable, on Administrator + Super Admin
   *   - `restricted: true`   → tenant-grantable, off Administrator, on Super Admin
   *   - `tier: 'platform'`   → NOT tenant-grantable, off both Admin and Super Admin
   *
   * `restricted` is for capabilities a tenant admin should be aware of but
   * not the default grant — today just `user.impersonate`.
   */
  readonly restricted?: boolean;
}

// Laid out by section (PERMISSION_SECTIONS below), then by group within it, so
// related permissions sit together here as they do in the role editor. The
// editor's order comes from PERMISSION_SECTIONS, not from this array; within a
// group, the action order (View, Edit, …) follows the array.
export const SYSTEM_ROLE_PERMISSIONS = [
  // ======================================================================
  // People — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- Employee ---------------------------------------------------------
  // Scope `my_team` resolves to the caller's recursive HR reporting subtree
  // (view scopes include the caller, edit scopes exclude them); `all` is
  // unrestricted. There is no customer-team reach here — employee visibility
  // is HR-hierarchy based.
  { id: 'user.view', group: 'Employee', label: 'View', scopes: ['all', 'my_team'] },
  { id: 'user.edit', group: 'Employee', label: 'Edit', scopes: ['all', 'my_team'] },
  // Admin-tier employee controls layered on top of `user.edit`, mirroring
  // `customer.admin`. Today it gates the historical amend (D10): editing what a
  // PAST version of the record says, which is a different authority from
  // editing the record — an ordinary edit changes it from today, an amend
  // changes what every report over that version's period has always said.
  // Presence-only; WHICH employees are reachable still derives from the
  // caller's `user.view` scope, because the amend runs `getById` first.
  { id: 'user.admin', group: 'Employee', label: 'Admin', scopes: [] },
  // Assume another user's identity for testing / support. The session
  // stays on the real human; an `impersonated_ba_user_id` column on
  // `ba_session` rides on top. Every audit row records the real human
  // as actor + the impersonated user separately. Single-level only —
  // an impersonator can't chain into another impersonation.
  //
  // `restricted: true` keeps it OFF the regular Administrator role; only
  // the Super Admin role (seeded alongside Administrator on every new
  // tenant) gets it by default.
  { id: 'user.impersonate', group: 'Employee', label: 'Impersonate', scopes: [], restricted: true },

  // ---- User Cost --------------------------------------------------------
  // Compensation data on a user's record — separated from the broader
  // user.view / user.edit gate because cost is commercially sensitive
  // and not every viewer should see payroll-equivalent figures via the
  // export or the cost history table. The Employee Cost column in
  // GET /api/users/export is omitted when the caller lacks user.cost.view;
  // GET/POST /api/user-employee-costs gate on these directly.
  //
  // Granted on Administrator + Super Admin by default (the bootstrap
  // seed pulls from FULL_REGISTRY_GRANT). For other roles, an admin
  // must explicitly grant.
  {
    id: 'user.cost.view',
    group: 'Employee',
    label: 'View cost',
    scopes: ['all', 'my_team'],
  },
  {
    id: 'user.cost.edit',
    group: 'Employee',
    label: 'Edit cost',
    scopes: ['all', 'my_team'],
  },

  // ---- Employee PII -----------------------------------------------------
  // An employee's home address (address lines, city, state, zip, country)
  // and free-text location label — personally identifying data separated
  // from the broader user.view gate because not every viewer should see it.
  //
  // Presence-only (no scope): the address columns are redacted server-side
  // from the detail, list, and export payloads for callers without it, and
  // the detail page renders a locked placeholder in place of the value. A
  // caller ALWAYS sees their OWN address regardless of the permission.
  //
  // Granted on Administrator + Super Admin by default (the bootstrap seed
  // pulls from FULL_REGISTRY_GRANT). For other roles, an admin must grant.
  { id: 'user.pii.view', group: 'Employee', label: 'View PII (address)', scopes: [] },

  // ======================================================================
  // Customers — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- Customer ---------------------------------------------------------
  {
    id: 'customer.view',
    group: 'Customer',
    label: 'View',
    scopes: ['all', 'my_team'],
  },
  {
    id: 'customer.edit',
    group: 'Customer',
    label: 'Edit',
    scopes: ['all', 'my_team'],
  },
  // Admin-only customer controls layered on top of `customer.edit`. Today it
  // gates the "internal customer" designation (seeing + toggling the flag) —
  // a tenant-wide structural setting, not a per-record edit. Enforced in the
  // service via `assertPermission` (no dedicated route), mirrored in the UI.
  // It also gates the historical amend (D10) — see `user.admin` for why that is
  // an admin-tier authority rather than part of `customer.edit`.
  { id: 'customer.admin', group: 'Customer', label: 'Admin', scopes: [] },

  // ---- Customer SOW -----------------------------------------------------
  // Presence-only: which customers' SOWs a caller may see/edit derives from
  // the caller's `customer.view` / `customer.edit` scope (a SOW is a child of
  // a customer, reachable only through it). See docs/permissions.md.
  { id: 'customer.sow.view', group: 'Customer Engagement', label: 'View', scopes: [] },
  { id: 'customer.sow.edit', group: 'Customer Engagement', label: 'Edit', scopes: [] },

  // ---- Customer Team ----------------------------------------------------
  // Gates the per-customer team-membership tab. Presence-only — which
  // customers' teams a caller may see/edit derives from the caller's
  // `customer.view` / `customer.edit` scope. (Billing rate is no longer a
  // membership attribute, so there is no separate rate-visibility gate.)
  { id: 'customer.team.view', group: 'Customer Team', label: 'View', scopes: [] },
  { id: 'customer.team.edit', group: 'Customer Team', label: 'Edit', scopes: [] },

  // ---- Customer Contact -------------------------------------------------
  // Gates the per-customer contacts tab (billing / alternate contact people).
  // Presence-only — which customers derives from `customer.view`/`customer.edit`.
  { id: 'customer.contact.view', group: 'Customer Contact', label: 'View', scopes: [] },
  { id: 'customer.contact.edit', group: 'Customer Contact', label: 'Edit', scopes: [] },

  // ---- Customer Budget --------------------------------------------------
  // Gates the per-customer budgets tab (hours allowed per service role per
  // period) and the over-budget figures shown during timesheet entry and
  // approval. Presence-only — which customers' budgets a caller may see/edit
  // derives from the caller's `customer.view` / `customer.edit` scope.
  //
  // `.view` is a genuine data gate, not just a screen gate: without it a user
  // still learns that an entry is over budget (they have to, or they can't
  // act on it) but never sees the pool size or the hours consumed. The API
  // scrubs those figures rather than the UI hiding them.
  { id: 'customer.budget.view', group: 'Customer Budget', label: 'View', scopes: [] },
  { id: 'customer.budget.edit', group: 'Customer Budget', label: 'Edit', scopes: [] },

  // ---- Customer Expense -------------------------------------------------
  // Gates the pass-through pane on the per-customer Invoicing tab — the lines
  // that seed each generated invoice with the items this customer is billed
  // on for. Presence-only — which customers' lines a caller may see/edit
  // derives from the caller's `customer.view` / `customer.edit` scope, the
  // same pattern as `customer.budget.*` and `customer.team.*`.
  //
  // Deliberately `customer.expense.*` rather than an invoicing-prefixed id:
  // a module prefix marks TENANT-wide reference data a holder sees in full,
  // whereas these ids are customer-scoped and read their scope off
  // `customer.*`. Grouping them with the other per-customer tabs is what makes
  // the role editor legible.
  { id: 'customer.expense.view', group: 'Customer Expense', label: 'View', scopes: [] },
  { id: 'customer.expense.edit', group: 'Customer Expense', label: 'Edit', scopes: [] },

  // ======================================================================
  // Time — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- Timesheet --------------------------------------------------------
  {
    id: 'timesheet.view',
    group: 'Timesheet',
    label: 'View',
    scopes: ['all', 'my_team'],
  },
  {
    id: 'timesheet.edit',
    group: 'Timesheet',
    label: 'Edit',
    scopes: ['all', 'my_team'],
  },
  {
    id: 'timesheet.submit_for',
    group: 'Timesheet',
    label: 'Submit for',
    scopes: ['all', 'my_team'],
  },
  // Gates the /approvals page and the underlying
  // /api/timesheet-approvals/* routes. The data layer still scopes to
  // "rows where the caller is a pending approver" — this permission
  // controls who sees the surface at all (so non-approvers don't get a
  // sidebar item that always reads empty). Decision authorization
  // continues to come from `approvers` membership at the service layer.
  {
    id: 'timesheet.approve',
    group: 'Timesheet',
    label: 'Approve',
    scopes: ['all', 'my_team'],
  },
  // Gates updating the tenant-wide timesheet lock settings
  // (PUT /api/timesheet-settings). Reading the settings only needs
  // `timesheet.view` so the timesheet grid can surface the next lock
  // date; mutating them is reserved for this permission.
  {
    id: 'timesheet.settings',
    group: 'Timesheet',
    label: 'Settings',
    scopes: [],
  },
  // Timesheet administration. Formerly gated the manual
  // POST /api/timesheets/reminders/send trigger, which has been removed now
  // that reminders are sent entirely by the automated cron fanout (which
  // does NOT consult this permission). Retained as a reserved grant for
  // future tenant-wide timesheet admin actions; currently gates nothing.
  {
    id: 'timesheet.admin',
    group: 'Timesheet',
    label: 'Admin',
    scopes: [],
  },

  // ---- Timer ------------------------------------------------------------
  // In-progress time tracking. Timers are personal — the service rejects
  // mutations of someone else's timer regardless of permission, so these
  // are effectively per-user toggles for whether the feature is available.
  { id: 'timer.view', group: 'Timer', label: 'View', scopes: [] },
  { id: 'timer.edit', group: 'Timer', label: 'Edit', scopes: [] },

  // ---- Notifications ----------------------------------------------------
  // .view gates the inbox + templates list; .edit gates updating personal
  // delivery preferences. Routes live in apps/notifications and are
  // mounted under /api/notifications/* — both call requirePermission().
  { id: 'notifications.view', group: 'Notifications', label: 'View', scopes: [] },
  { id: 'notifications.edit', group: 'Notifications', label: 'Edit', scopes: [] },

  // ======================================================================
  // Invoicing — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- Invoice ----------------------------------------------------------
  // The invoice list, the editor, and the two state transitions.
  //
  // Presence-only, like the other per-customer surfaces: WHICH customers'
  // invoices a caller may see or edit derives from their `customer.view` /
  // `customer.edit` scope, enforced in `InvoiceService` via
  // `CustomerAccessService`. Declaring scopes here as well would be a second
  // source of truth that can contradict the first.
  //
  // Four ids rather than the usual two, because these are four genuinely
  // different authorities:
  //   - `.edit` fixes a typo in a draft.
  //   - `.approve` is a financial sign-off — it says an amount is correct and
  //     ready to be billed, and it is what the bulk-approve queue gates on.
  //   - `.send` puts a document in a customer's inbox. Irreversible in the way
  //     that matters: you cannot unsend it, and `sent` is terminal.
  // Someone who can correct a draft should not thereby be able to bill a
  // customer, and someone who approves amounts is not necessarily the person
  // who mails them.
  //
  // All four auto-grant to the seeded Administrator (FULL_REGISTRY_GRANT) and
  // Super Admin (SUPER_ADMIN_GRANT), which covers NEW tenants only; the PR's
  // Migration section backfills them onto those roles for existing ones.
  { id: 'invoice.view', group: 'Invoice', label: 'View', scopes: [] },
  { id: 'invoice.edit', group: 'Invoice', label: 'Edit', scopes: [] },
  { id: 'invoice.approve', group: 'Invoice', label: 'Approve', scopes: [] },
  { id: 'invoice.send', group: 'Invoice', label: 'Send', scopes: [] },

  // ---- Invoice Settings -------------------------------------------------
  // The tenant's issuer block (name, address, phone, website), the remittance
  // instructions printed on every invoice, and the defaults a new invoice
  // inherits (terms, due-day offset, currency, numbering start, retainer
  // invoice mode). Tenant-wide reference data, so no scopes.
  //
  // Split out from `invoice.*` on purpose. The payment-details block is a
  // bank account number, an ABA routing number and a Zelle address; the set
  // of people who need to read an invoice is far wider than the set who
  // should see — let alone change — where the money lands. Anyone who can
  // edit these can silently redirect every future payment, which makes this
  // the most sensitive pair in the `invoice.*` family.
  //
  // Auto-grants to the seeded Administrator (FULL_REGISTRY_GRANT) and Super
  // Admin (SUPER_ADMIN_GRANT), which covers NEW tenants only; the PR's
  // Migration section backfills both ids onto those roles for existing ones.
  { id: 'invoice.settings.view', group: 'Invoice Settings', label: 'View', scopes: [] },
  { id: 'invoice.settings.edit', group: 'Invoice Settings', label: 'Edit', scopes: [] },

  // ---- Ratecard ---------------------------------------------------------
  // Tenant-singleton T&M rate card. Read gates the Setup → Ratecard page
  // (including past/future as-of views); edit gates creating the initial
  // card and amending it (each amendment is a new version).
  { id: 'ratecard.view', group: 'Ratecard', label: 'View', scopes: [] },
  { id: 'ratecard.edit', group: 'Ratecard', label: 'Edit', scopes: [] },

  // ---- Expense Catalog --------------------------------------------------
  // Gates the Setup → Expense Catalog page: the tenant's master list of
  // pass-through items (software seats, subscriptions, filing fees…) that can
  // later be associated with customers and billed on. Tenant-wide reference
  // data, not per-customer, so no scopes — a holder sees/edits the whole
  // catalog. `.edit` additionally gates import.
  //
  // Entity-named (`expensecatalog.*`), like `ratecard.*`, rather than carrying
  // an invoicing prefix. The catalog is tenant-wide reference data in its own
  // right — it lives under /setup and is edited by people who never open an
  // invoice — so naming it after the module it feeds would overstate the tie.
  // Auto-grants to the seeded Administrator (FULL_REGISTRY_GRANT) and Super Admin
  // (SUPER_ADMIN_GRANT); the PR's Migration section backfills both ids onto
  // those roles for existing tenants.
  { id: 'expensecatalog.view', group: 'Expense Catalog', label: 'View', scopes: [] },
  { id: 'expensecatalog.edit', group: 'Expense Catalog', label: 'Edit', scopes: [] },

  // ---- Chart of Accounts ------------------------------------------------
  // Gates Setup → Chart of accounts: the read-only list of income accounts
  // invoice lines are booked to. View only — the chart is maintained in the
  // books, never edited in the app. Tenant-wide reference data, so no scopes.
  // The list endpoint ALSO answers to invoice.view, invoice.settings.view and
  // activity.view, because the editor, the settings page and the activity
  // form all need its pickers. Auto-grants to the seeded Administrator and
  // Super Admin for new tenants; the PR's Migration section backfills existing
  // ones.
  { id: 'chart_of_accounts.view', group: 'Chart of Accounts', label: 'View', scopes: [] },

  // ======================================================================
  // Reports — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- Reports ----------------------------------------------------------
  // Gates the Reports section (sidebar item + /api/reports/* and
  // /api/analytics/* routes). Presence-only.
  //
  // WHICH ROWS a report returns is not this permission's question. It follows
  // the scopes the caller already holds, resolved once in
  // `core/reports/reach.ts` and used by every report (the Timesheet report,
  // every analytics model, dashboard tiles, exports):
  //   - `user.view` All          → every row.
  //   - otherwise the union of   → rows logged by the caller and their team
  //                                (`user.view` My team), and rows on the
  //                                customers the caller LEADS (when they hold
  //                                `customer.view`).
  //   - neither held             → no rows.
  // It used to carry its own scope (all / my_organization / my_teams /
  // my_organization_my_teams) — a second, separately-edited answer to "whom
  // can this person see", which nobody could explain next to the first.
  // Auto-grants to the seeded Administrator (FULL_REGISTRY_GRANT) and Super
  // Admin (SUPER_ADMIN_GRANT).
  { id: 'report.view', group: 'Report', label: 'View', scopes: [] },

  // KEEPING a report: the builder page (/reports/new) and every write on
  // /api/report-definitions. Held IN ADDITION to `report.view`, never instead
  // of it — the report reach (`core/reports/reach.ts`) still decides which rows
  // any report returns.
  //
  // NOT running one. The analytics endpoints ask only for `report.view`,
  // because someone holding it alone must be able to open a report shared with
  // them, change its filters and dates while looking at it, and export
  // (CR-1.5). This id is the line between running and saving, and it used to
  // sit one step too far left.
  //
  // `edit` rather than a verb of its own, even though nothing is saved yet:
  // `edit` already means create-and-update everywhere in this registry (there
  // is no `create` anywhere), and when `report_definitions` lands, saving a
  // report is this same id. A separate `report.build` would leave two ids over
  // one capability and no answer to "may I edit a saved report without being
  // able to build one". The second axis, when it comes, is SHARING.
  //
  // Scopeless. Which rows a report returns has one answer, the report reach;
  // a scope here would be a second one.
  { id: 'report.edit', group: 'Report', label: 'Edit', scopes: [] },

  // The second axis the note above promised: giving OTHER PEOPLE a report you
  // saved. One verb whether the recipient is one person, a role, or everyone
  // (CR-1.3) — the three differ only in who is named, and splitting them would
  // be three ids over one decision.
  //
  // What it grants is the right to hand over a DEFINITION, never a result set:
  // a recipient runs the report themselves, under their own `report.view`
  // scope, so sharing can never widen what anybody can see (CR-3.1). That is
  // why this is scopeless too — there is no disclosure here for a scope to
  // narrow.
  //
  // It also gates the compiled-SQL disclosure on `POST /api/analytics/query`.
  // The statement names the warehouse's tables and columns, which is a
  // different kind of thing from the rows a report returns, and this is the
  // widest grant in the group — so requiring it ON TOP of `report.edit` keeps
  // "may compose a report" and "may read how a report is compiled" as separate
  // answers.
  { id: 'report.share', group: 'Report', label: 'Share', scopes: [] },

  // STANDARD LABELS: the company-wide ones on the Reports page (Budgets vs
  // Actuals, Utilization, Timesheet…) — creating, renaming and deleting them,
  // and putting a report under one. Personal labels and stars need nothing
  // beyond `report.view`: they organise one person's own list and nobody else
  // ever sees them.
  //
  // A label NEVER GRANTS ACCESS. A report sits under a standard label for the
  // people it is already shared with and for nobody else, so this id decides
  // where a report is filed, not who may open it — that stays `report.share`'s
  // question alone. It is separate from `report.share` because the two are
  // different jobs: sharing hands over your own report, and this curates
  // everybody's list.
  //
  // Scopeless: there are no rows here for a scope to narrow.
  { id: 'report.label', group: 'Report', label: 'Manage standard labels', scopes: [] },

  // ---- Dashboard ------------------------------------------------------
  // REACHING THE DASHBOARD SURFACE AT ALL: /dashboards in the sidebar, the
  // route behind it, and every read on /api/dashboards.
  //
  // A NARROWING GATE, NEVER A SUBSTITUTE. It is asked for IN ADDITION to
  // `report.view`, never instead of it: a tile's numbers come from
  // `POST /api/analytics/query`, which asks for `report.view` and returns only
  // the rows the caller's report reach allows.
  // Holding this one alone opens nothing, so it can only ever subtract from
  // what `report.view` already allows — which is what keeps it from being a
  // second, contradictory answer to "what may this caller see".
  //
  // It exists because "may build reports" and "may see the Dashboards section"
  // turned out to be questions a tenant wants to answer separately. Until this
  // id there was no way to say no to the second without also saying no to the
  // first: the section was gated on `report.view`, the SAME id as Reports, so
  // every user who could open a report also had Dashboards in their sidebar.
  // The registry had no vocabulary for that, and an admin looking for the
  // switch could not find one because none existed.
  //
  // Scopeless, for the same reason `report.edit` is: the tiles' rows follow
  // the report reach, and a scope here would be a second answer.
  { id: 'dashboard.view', group: 'Dashboard', label: 'View', scopes: [] },

  // COMPOSING A DASHBOARD: every write on /api/dashboards. The exact twin of
  // `report.edit`, one artefact along — a dashboard is a saved arrangement of
  // saved reports, owned by one person and shared the same three ways.
  //
  // Held on top of `dashboard.view` (which is itself held on top of
  // `report.view`). The role editor shows them as the two adjacent groups of
  // the Reports section — Report, then Dashboard — in the order they compose:
  // see the section, then save into it.
  //
  // Scopeless, for the same reason `report.edit` is: the tiles' rows follow
  // the report reach, and a scope here would be a second answer.
  { id: 'dashboard.edit', group: 'Dashboard', label: 'Edit', scopes: [] },

  // Giving other people a dashboard you built. Separate from `report.share`
  // because the two hand over different things and a tenant may reasonably
  // want one without the other: sharing a dashboard publishes an ARRANGEMENT,
  // and its tiles keep pointing at reports whose own sharing has not changed —
  // a recipient who cannot reach a referenced report sees a tombstone in that
  // tile rather than its contents, which is what keeps this grant from being a
  // back door around `report.share`.
  { id: 'dashboard.share', group: 'Dashboard', label: 'Share', scopes: [] },

  // ======================================================================
  // Setup — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- Company ----------------------------------------------------------
  // Gates the Setup → Company page: the read-only company profile
  // (name + email domains, read via GET /api/company) and the login-history
  // CSV export (GET /api/company/login-history/export). Tenant-tier — company
  // data is tenant-scoped, so each tenant's admin may hold it. Auto-grants to
  // the seeded Administrator (FULL_REGISTRY_GRANT) and Super Admin
  // (SUPER_ADMIN_GRANT); the PR's Migration section backfills it onto those
  // two roles for existing tenants. Distinct from the platform-tier `tenant.*`
  // pair below, which is NOT tenant-grantable.
  { id: 'company.view', group: 'Company', label: 'View', scopes: [] },

  // ---- Activity ----------------------------------------------------------
  { id: 'activity.view', group: 'Activity', label: 'View', scopes: [] },
  { id: 'activity.edit', group: 'Activity', label: 'Edit', scopes: [] },

  // ---- Service Role -----------------------------------------------------
  { id: 'service_role.view', group: 'Service Role', label: 'View', scopes: [] },
  { id: 'service_role.edit', group: 'Service Role', label: 'Edit', scopes: [] },

  // ---- Department -------------------------------------------------------
  { id: 'department.view', group: 'Department', label: 'View', scopes: [] },
  { id: 'department.edit', group: 'Department', label: 'Edit', scopes: [] },

  // ---- Skills -----------------------------------------------------------
  { id: 'skills.view', group: 'Skills', label: 'View', scopes: [] },
  { id: 'skills.edit', group: 'Skills', label: 'Edit', scopes: [] },
  // Gates the "My Skills" self-rating surface — nav item + the self-rating
  // routes. Every row a caller touches there is still their OWN (the
  // service never takes a target userId), so this isn't an access-scope
  // control; it's presence-only, same shape as every other nav permission.
  { id: 'skills.rate', group: 'Skills', label: 'Rate own skills', scopes: [] },
  // Gates the Team Approvals *surface* only (nav item + this module's own
  // routes) — the generic approve/reject decide endpoints the frontend
  // actually calls carry no permission gate by design; `approvers` table
  // membership is the authorization. Same two-layer split as
  // `timesheet.approve`.
  { id: 'skills.approve', group: 'Skills', label: 'Approve ratings', scopes: [] },

  // ---- Holiday ----------------------------------------------------------
  // Gates the Setup → Holidays calendar: the per-country list of non-working
  // dates. Tenant-wide reference data (not per-user), so no scopes — a holder
  // sees/edits the whole tenant's calendar. Auto-grants to the seeded
  // Administrator (FULL_REGISTRY_GRANT) and Super Admin (SUPER_ADMIN_GRANT);
  // the PR's Migration section backfills both ids onto those roles for
  // existing tenants. The calendar is read-only reference data today; the
  // working-days-per-month calculation that consumes it lands separately.
  { id: 'holiday.view', group: 'Holiday', label: 'View', scopes: [] },
  { id: 'holiday.edit', group: 'Holiday', label: 'Edit', scopes: [] },

  // ---- Custom Fields ----------------------------------------------------
  // Gates definition management in Setup. Reading and writing custom-field
  // *values* on host entities flows through the host's own permission
  // (user.view/edit, customer.view/edit, customer.sow.view/edit).
  { id: 'custom_fields.view', group: 'Custom Fields', label: 'View', scopes: [] },
  { id: 'custom_fields.edit', group: 'Custom Fields', label: 'Edit', scopes: [] },

  // ---- Approval Policy --------------------------------------------------
  // Gates the admin UI under /approval-policies. Decision authorization
  // (approve/reject on an instance) is NOT permission-driven — it comes
  // from membership in the `approvers` table for the active step. See
  // typescript/docs/approvals.md.
  { id: 'approval_policy.view', group: 'Approval Policy', label: 'View', scopes: [] },
  { id: 'approval_policy.edit', group: 'Approval Policy', label: 'Edit', scopes: [] },

  // ======================================================================
  // Administration — see PERMISSION_SECTIONS
  // ======================================================================
  // ---- System Role ------------------------------------------------------
  { id: 'system_role.view', group: 'System Role', label: 'View', scopes: [] },
  { id: 'system_role.edit', group: 'System Role', label: 'Edit', scopes: [] },

  // ---- Business units ---------------------------------------------------
  // The companies inside one tenant (docs/business-units.md). `view` opens
  // Setup → Business units; the Administrator holds it (migration 0055 grants
  // it to the roles that edit roles), not every role.
  // `edit` MANAGES BUSINESS UNITS: adds and edits them, grants and removes
  // access to them, changes a primary employer — within the units its holder
  // reaches; it never widens what they see (only `all_units` reaches every
  // unit). Restricted: not on the Administrator by
  // default — the Super Admin has it, and an owner may grant it to a role.
  // The Super Admin role itself stays the Super Admin's to give.
  { id: 'business_unit.view', group: 'Business Unit', label: 'View', scopes: [] },
  {
    id: 'business_unit.edit',
    group: 'Business Unit',
    label: 'Edit',
    scopes: [],
    restricted: true,
  },
  // `all_units` IS WHAT MAKES SOMEONE A SUPER ADMIN: the role holding it is
  // the role they act under in EVERY Business Unit, not only their primary
  // employer. Nothing else — no role name, no flag — does. Restricted, and
  // like every restricted permission it can be put on a role, or a role
  // carrying it given to someone, only by a holder.
  {
    id: 'business_unit.all_units',
    group: 'Business Unit',
    label: 'All Business Units',
    scopes: [],
    restricted: true,
  },

  // ---- Audit ------------------------------------------------------------
  // Gates the platform-wide Audit Log screen + every /api/audit read. It is
  // the "may you call /api/audit at all" gate; the entity's own `.view` is
  // still required per row, so audit.view never widens what fields a caller
  // can see — when listing across types the result is restricted to the
  // entity types the caller already has `.view` on. Tenant-tier: each tenant
  // sees only its own tenant-scoped audit trail, so it auto-grants to the
  // seeded Administrator (FULL_REGISTRY_GRANT) and Super Admin
  // (SUPER_ADMIN_GRANT); the PR's Migration section backfills it onto those
  // two roles for existing tenants.
  { id: 'audit.view', group: 'Audit', label: 'View', scopes: [] },

  // ---- Announcement -------------------------------------------------------
  // Gates the Setup → Announcements admin screen (manage site-wide banners).
  // Tenant-wide broadcast content, not per-user, so no scopes.
  { id: 'announcement.view', group: 'Announcement', label: 'View', scopes: [] },
  { id: 'announcement.edit', group: 'Announcement', label: 'Edit', scopes: [] },
  // No id for reading the live-banner feed: every signed-in user reads it,
  // ungated (`GET /api/announcements/live` sits behind the session guard
  // only). A permission for it meant every new custom role silently lost the
  // banners until someone remembered to tick it.

  // ---- Release Note -------------------------------------------------------
  // Reading What's New is ungated for every signed-in user, for the same
  // reason as the live-banner feed above — so there is no `.view` id. Only
  // `.edit` (add/edit/delete/restore, and the audit trail) is permissioned.
  { id: 'release_note.edit', group: 'Release Note', label: 'Edit', scopes: [] },

  // No id for the in-app "Send feedback" widget: every signed-in user may
  // send feedback (`POST /api/feedback` sits behind the session guard only),
  // for the same reason as the live-banner feed above. It used to be
  // `feedback.send`, granted to every role, which made it a switch nobody
  // wanted to turn off and every new custom role had to remember to tick.

  // ---- Tenant — platform-tier --------------------------------------------
  // `tenant.edit` lets an admin rename their own tenant. The route layer
  // refuses to touch `domains[]` (which would re-route SSO across tenants).
  // Platform-tier keeps it out of the tenant role editor entirely.
  { id: 'tenant.view', group: 'Tenant', label: 'View', scopes: [], tier: 'platform' },
  { id: 'tenant.edit', group: 'Tenant', label: 'Edit', scopes: [], tier: 'platform' },
] as const satisfies readonly PermissionDef[];

/**
 * How the role editor lays the registry out: a few sections, each a run of
 * related groups. Before this the editor listed groups in registry order, which
 * had grown by accretion — Ratecard and Invoice sat between Customer Contact and
 * Customer Team, Notifications between Audit and Report — so finding "everything
 * about customers" meant scrolling the whole sheet.
 *
 * A `permissions.test.ts` invariant pins that every group in the registry sits
 * in exactly one section and every named group exists, so a new group fails CI
 * until it is placed.
 */
export const PERMISSION_SECTIONS: readonly {
  title: string;
  groups: readonly PermissionGroup[];
}[] = [
  { title: 'People', groups: ['Employee'] },
  {
    title: 'Customers',
    groups: [
      'Customer',
      'Customer Engagement',
      'Customer Team',
      'Customer Contact',
      'Customer Budget',
      'Customer Expense',
    ],
  },
  { title: 'Time', groups: ['Timesheet', 'Timer', 'Notifications'] },
  {
    title: 'Invoicing',
    groups: ['Invoice', 'Invoice Settings', 'Ratecard', 'Expense Catalog', 'Chart of Accounts'],
  },
  { title: 'Reports', groups: ['Report', 'Dashboard'] },
  {
    title: 'Setup',
    groups: [
      'Company',
      'Activity',
      'Service Role',
      'Department',
      'Skills',
      'Holiday',
      'Custom Fields',
      'Approval Policy',
    ],
  },
  {
    title: 'Administration',
    groups: ['System Role', 'Business Unit', 'Audit', 'Announcement', 'Release Note', 'Tenant'],
  },
];

/** Literal union of every permission group name — what PERMISSION_SECTIONS
 *  names, so a typo or a group rename fails to compile. */
export type PermissionGroup = (typeof SYSTEM_ROLE_PERMISSIONS)[number]['group'];

/** Literal union of every system-role permission id. Gives compile-time
 *  safety to code that names specific permissions (`requirePermission('customer.edit')`). */
export type SystemRolePermissionId = (typeof SYSTEM_ROLE_PERMISSIONS)[number]['id'];

/**
 * THE PERMISSIONS A PERSON MAY USE FROM ANY BUSINESS UNIT THEY WORK IN
 * (docs/business-units.md → "Route gates").
 *
 * A route gated by one of these lets the caller in when the role they hold in
 * ANY of their units grants it — their primary employer's, a unit they were
 * granted, or every unit with `business_unit.all_units` — because the code
 * behind every such route then decides record by record, in the record's own
 * unit (`CustomerAccessService`, `UserAccessService`, the system-roles routes).
 * Every other permission gates on the person's primary role, as before.
 *
 * The customer sub-records (contacts, team, SOWs, budgets, expenses) are
 * here on the same terms: their routes check the customer through
 * `CustomerAccessService`, which also requires the gate's own permission in
 * THE CUSTOMER'S unit (the route records it — `OperationContext.unitGates`).
 *
 * Add an id only when EVERY route it gates checks the record's unit;
 * `middleware/permissions-any-unit.test.ts` holds the routes to that. A tenant
 * with one unit answers exactly as before: there, any unit is the primary one.
 *
 * Not here, deliberately: the admin tier (`customer.admin`, `user.admin` —
 * correcting history is checked on the primary role inside the service),
 * cost and PII, and the Business Unit permissions themselves.
 */
/**
 * The customer SUB-RECORD permissions — contacts, team, SOWs, budgets,
 * expenses. A route they gate opens on a role from any unit; then the role of
 * EACH CUSTOMER'S unit must hold the same permission (the route records it on
 * `OperationContext.unitGates`, `CustomerAccessService` requires it).
 */
export const CUSTOMER_RECORD_PERMISSIONS = [
  'customer.contact.view',
  'customer.contact.edit',
  'customer.team.view',
  'customer.team.edit',
  'customer.sow.view',
  'customer.sow.edit',
  'customer.budget.view',
  'customer.budget.edit',
  'customer.expense.view',
  'customer.expense.edit',
] as const satisfies readonly SystemRolePermissionId[];

const CUSTOMER_RECORD_SET: ReadonlySet<string> = new Set(CUSTOMER_RECORD_PERMISSIONS);

/** Whether `id` is a customer sub-record permission ({@link CUSTOMER_RECORD_PERMISSIONS}). */
export function isCustomerRecordPermission(id: string): boolean {
  return CUSTOMER_RECORD_SET.has(id);
}

export const ANY_UNIT_PERMISSIONS = [
  'customer.view',
  'customer.edit',
  ...CUSTOMER_RECORD_PERMISSIONS,
  'user.view',
  'user.edit',
  'system_role.view',
  'system_role.edit',
] as const satisfies readonly SystemRolePermissionId[];

const ANY_UNIT_SET: ReadonlySet<string> = new Set(ANY_UNIT_PERMISSIONS);

/** Whether a route gated by `id` accepts the role of any of the caller's units. */
export function isAnyUnitPermission(id: string): boolean {
  return ANY_UNIT_SET.has(id);
}

/** Default registry used when callers don't supply one — covers system roles
 *  today. Future role-shaped consumers can pass their own. */
export const PERMISSION_REGISTRY: readonly PermissionDef[] = SYSTEM_ROLE_PERMISSIONS;

/**
 * The full registry expressed as a grantable `PermissionEntry[]`. Scoped
 * permissions default to `scope: 'all'` (the broadest grant); global
 * permissions omit `scope`. Used by tenant bootstrap to seed an
 * `Administrator` role that can immediately drive the system — and by the
 * "grant Admin everything" migration in the permissions PR.
 *
 * Updating the registry above auto-updates this constant; nothing to
 * synchronize.
 */
export const FULL_REGISTRY_GRANT: readonly PermissionEntry[] = (
  SYSTEM_ROLE_PERMISSIONS as readonly PermissionDef[]
)
  .filter((p) => !p.restricted)
  .map((p) =>
    p.scopes.length > 0 ? { permission: p.id, scope: 'all' as const } : { permission: p.id },
  );

/**
 * Super Admin grant — strict superset of `FULL_REGISTRY_GRANT`. Includes
 * every permission in the registry, restricted + platform-tier alike, so
 * Super Admin can never be less powerful than Administrator on any axis.
 *
 * Today the only addition over Admin is `user.impersonate` (the sole
 * `restricted` permission). Adding new permissions auto-extends this
 * constant; nothing else to synchronize.
 */
export const SUPER_ADMIN_GRANT: readonly PermissionEntry[] = (
  SYSTEM_ROLE_PERMISSIONS as readonly PermissionDef[]
).map((p) =>
  p.scopes.length > 0 ? { permission: p.id, scope: 'all' as const } : { permission: p.id },
);

export type PermissionRegistry = readonly PermissionDef[];

/** Build a `{id → def}` lookup once per registry. */
export function indexRegistry(registry: PermissionRegistry): ReadonlyMap<string, PermissionDef> {
  return new Map(registry.map((p) => [p.id, p]));
}

const DEFAULT_INDEX = indexRegistry(PERMISSION_REGISTRY);

export function lookupPermission(id: string, registry?: PermissionRegistry): PermissionDef | null {
  const index = registry ? indexRegistry(registry) : DEFAULT_INDEX;
  return index.get(id) ?? null;
}

export interface PermissionEntry {
  permission: string;
  scope?: PermissionScope;
}

/**
 * Validate a permissions array against a registry. Returns a tagged result
 * with per-index error paths so callers can surface inline.
 *
 * Errors carry the canonical `FieldErrorCode` vocabulary (`REQUIRED`,
 * `INVALID_ENUM_VALUE`, `UNIQUE_VIOLATION`, `INVALID_FORMAT`) — the service
 * can spread them straight into a `ValidationError.fields` without translating.
 *
 * `tier` defaults to `'tenant'` (the strict path used by HTTP routes that
 * are exposed to tenant admins). Pass `'platform'` to permit grants of
 * `tier: 'platform'` permission ids — used by bootstrap code that seeds
 * tenants with the full registry. NEVER use `'platform'` from an HTTP-
 * driven path.
 */
export function validatePermissions(
  entries: readonly unknown[],
  registry: PermissionRegistry = PERMISSION_REGISTRY,
  tier: PermissionTier = 'tenant',
): { ok: true; value: PermissionEntry[] } | { ok: false; errors: FieldError[] } {
  const index = registry === PERMISSION_REGISTRY ? DEFAULT_INDEX : indexRegistry(registry);
  const errors: FieldError[] = [];
  const seen = new Set<string>();
  const value: PermissionEntry[] = [];

  entries.forEach((raw, i) => {
    if (!raw || typeof raw !== 'object') {
      errors.push({
        path: `permissions.${i}`,
        code: 'INVALID_FORMAT',
        message: 'must be an object',
      });
      return;
    }
    const e = raw as Record<string, unknown>;
    const permission = typeof e.permission === 'string' ? e.permission : '';
    if (!permission) {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'REQUIRED',
        message: 'required',
      });
      return;
    }
    const def = index.get(permission);
    if (!def) {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'INVALID_ENUM_VALUE',
        message: `unknown permission "${permission}"`,
      });
      return;
    }
    // Tier check — reject platform-tier grants when the caller is a tenant
    // admin. Bootstrap callers pass `tier: 'platform'` to bypass this.
    if (tier === 'tenant' && def.tier === 'platform') {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'INVALID_ENUM_VALUE',
        message: 'this permission is platform-only and cannot be granted from a tenant role',
      });
      return;
    }
    if (seen.has(permission)) {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'UNIQUE_VIOLATION',
        message: 'duplicate',
      });
      return;
    }
    seen.add(permission);

    const scopeRaw = e.scope;
    if (def.scopes.length === 0) {
      if (scopeRaw !== undefined && scopeRaw !== null) {
        errors.push({
          path: `permissions.${i}.scope`,
          code: 'INVALID_ENUM_VALUE',
          message: 'must be omitted (this permission has no scopes)',
        });
        return;
      }
      value.push({ permission });
      return;
    }

    if (scopeRaw === undefined || scopeRaw === null) {
      errors.push({
        path: `permissions.${i}.scope`,
        code: 'REQUIRED',
        message: `required; must be one of: ${def.scopes.join(', ')}`,
      });
      return;
    }
    if (typeof scopeRaw !== 'string' || !def.scopes.includes(scopeRaw as PermissionScope)) {
      errors.push({
        path: `permissions.${i}.scope`,
        code: 'INVALID_ENUM_VALUE',
        message: `must be one of: ${def.scopes.join(', ')}`,
      });
      return;
    }
    value.push({ permission, scope: scopeRaw as PermissionScope });
  });

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, value };
}

// ---------------------------------------------------------------------------
// Pure state-transition helpers — shared by the UI editor and other callers
// that mutate permissions arrays. Extracted so they can be unit-tested
// without rendering React.
// ---------------------------------------------------------------------------

/**
 * Toggle a permission on/off. When turning on a scoped permission, default
 * the scope to the broadest available (`all`, or the first declared scope).
 */
export function togglePermission(
  value: readonly PermissionEntry[],
  id: string,
  registry: PermissionRegistry = PERMISSION_REGISTRY,
): PermissionEntry[] {
  const has = value.some((e) => e.permission === id);
  if (has) return value.filter((e) => e.permission !== id);
  const def = lookupPermission(id, registry);
  if (!def) return [...value]; // unknown id is a no-op; caller surfaces validation errors
  const fallbackScope = def.scopes[0];
  const entry: PermissionEntry =
    def.scopes.length > 0 && fallbackScope
      ? { permission: id, scope: def.scopes.includes('all') ? 'all' : fallbackScope }
      : { permission: id };
  return [...value, entry];
}

/** Set the scope on an enabled entry. No-op if the entry isn't present. */
export function setPermissionScope(
  value: readonly PermissionEntry[],
  id: string,
  scope: PermissionScope,
): PermissionEntry[] {
  return value.map((e) => (e.permission === id ? { ...e, scope } : e));
}

/**
 * Reconcile a stored permissions array against the registry so it always
 * satisfies {@link validatePermissions}: drop entries for unknown ids and
 * duplicates, and read every stored scope through {@link canonicalScope} —
 * strip it from permissions the registry now declares scopeless, default a
 * missing one to `all`, and read a retired or unknown code as `my_team`
 * (never `all`: loading a Manager's role and saving it must not widen it).
 *
 * Used by the role editor when LOADING a role: a permission that lost its
 * scopes in a registry change (e.g. `customer.sow.view` going presence-only)
 * leaves stale `scope` on existing rows. Without this, the editor round-trips
 * that stale scope unchanged and the save 400s ("must be omitted"). Normalizing
 * on load makes any subsequent save valid regardless of stored staleness — the
 * data migration cleans the rows at rest; this keeps the UI robust meanwhile.
 */
export function normalizePermissions(
  entries: readonly PermissionEntry[],
  registry: PermissionRegistry = PERMISSION_REGISTRY,
): PermissionEntry[] {
  const index = registry === PERMISSION_REGISTRY ? DEFAULT_INDEX : indexRegistry(registry);
  const out: PermissionEntry[] = [];
  const seen = new Set<string>();
  for (const e of entries) {
    const def = index.get(e.permission);
    if (!def || seen.has(e.permission)) continue;
    seen.add(e.permission);
    const scope = scopeFor(def, e.scope);
    out.push(scope ? { permission: e.permission, scope } : { permission: e.permission });
  }
  return out;
}

/**
 * Group registry entries by their `group` for layout. Order within group
 * follows registry order.
 */
export function groupRegistry(
  registry: PermissionRegistry = PERMISSION_REGISTRY,
): Array<[string, PermissionDef[]]> {
  const map = new Map<string, PermissionDef[]>();
  for (const def of registry) {
    const list = map.get(def.group);
    if (list) list.push(def);
    else map.set(def.group, [def]);
  }
  return Array.from(map.entries());
}

export interface PermissionSection {
  title: string;
  groups: Array<[string, PermissionDef[]]>;
}

/**
 * The registry grouped ({@link groupRegistry}) and laid out in
 * {@link PERMISSION_SECTIONS} order — what the role editor and the role page
 * render. A group no section names (a custom registry's) lands in a trailing
 * "Other" section rather than vanishing; sections left with no groups are
 * dropped, so a caller that filters groups out first gets no empty headings.
 */
export function sectionRegistry(
  registry: PermissionRegistry = PERMISSION_REGISTRY,
  sections: readonly { title: string; groups: readonly string[] }[] = PERMISSION_SECTIONS,
): PermissionSection[] {
  const byGroup = new Map(groupRegistry(registry));
  const out: PermissionSection[] = [];
  for (const section of sections) {
    const groups: Array<[string, PermissionDef[]]> = [];
    for (const name of section.groups) {
      const defs = byGroup.get(name);
      if (!defs) continue;
      groups.push([name, defs]);
      byGroup.delete(name);
    }
    if (groups.length > 0) out.push({ title: section.title, groups });
  }
  if (byGroup.size > 0) out.push({ title: 'Other', groups: [...byGroup.entries()] });
  return out;
}

/**
 * Diff two permissions arrays. Returns granted/revoked/scope-changed entries
 * so callers (audit narrator, dirty checks) can describe the change.
 */
export interface PermissionsDiff {
  granted: PermissionEntry[];
  revoked: PermissionEntry[];
  scopeChanged: Array<{
    permission: string;
    from: PermissionScope | undefined;
    to: PermissionScope | undefined;
  }>;
}

export function diffPermissions(
  before: readonly PermissionEntry[],
  after: readonly PermissionEntry[],
): PermissionsDiff {
  const b = new Map(before.map((e) => [e.permission, e.scope]));
  const a = new Map(after.map((e) => [e.permission, e.scope]));
  const granted: PermissionEntry[] = [];
  const revoked: PermissionEntry[] = [];
  const scopeChanged: PermissionsDiff['scopeChanged'] = [];
  for (const id of new Set([...b.keys(), ...a.keys()])) {
    const had = b.has(id);
    const has = a.has(id);
    if (has && !had) {
      const scope = a.get(id);
      granted.push(scope ? { permission: id, scope } : { permission: id });
    } else if (had && !has) {
      const scope = b.get(id);
      revoked.push(scope ? { permission: id, scope } : { permission: id });
    } else if (had && has && b.get(id) !== a.get(id)) {
      scopeChanged.push({ permission: id, from: b.get(id), to: a.get(id) });
    }
  }
  return { granted, revoked, scopeChanged };
}
